"use client"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Mail, Send, Calendar, Users, CheckCircle2, XCircle, Loader2, RotateCcw } from "lucide-react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import toast from "react-hot-toast"

export function EmailManagement() {
  const [users, setUsers] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [roleFilter, setRoleFilter] = useState("")
  const [results, setResults] = useState(null)
  const [resendingUsers, setResendingUsers] = useState(new Set())

  // Fetch all users (always include sent)
  const fetchPendingUsers = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      
      if (roleFilter && roleFilter.trim() !== '') {
        params.append("role", roleFilter)
      }

      // Always include sent users
      params.append("includeSent", "true")

      const url = API_ENDPOINTS.usersPendingEmails(params.toString())

      const response = await axiosInstance.get(url)

      if (response.data.status === "success") {
        setUsers(response.data.data.users || [])
        setResults(null)
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch users")
    } finally {
      setLoading(false)
    }
  }

  // Load users on component mount and when filters change
  useEffect(() => {
    fetchPendingUsers()
  }, [roleFilter])

  // Handle individual checkbox
  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    )
  }

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(users.map((user) => user._id))
    } else {
      setSelectedUsers([])
    }
  }

  // Send batch emails
  const handleSendEmails = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user")
      return
    }

    try {
      setSending(true)
      const response = await axiosInstance.post(
        API_ENDPOINTS.usersSendBatchEmails,
        { userIds: selectedUsers }
      )

      if (response.data.status === "success") {
        toast.success(
          `Successfully sent ${response.data.data.sent} email(s). ${response.data.data.failed} failed.`
        )
        setResults(response.data.data)
        setSelectedUsers([])
        // Refresh the list
        await fetchPendingUsers()
      }
    } catch (error) {
      console.error("Error sending emails:", error)
      toast.error(error.response?.data?.message || "Failed to send emails")
    } finally {
      setSending(false)
    }
  }

  // Resend credentials email for a single user
  const handleResendEmail = async (userId) => {
    try {
      setResendingUsers((prev) => new Set(prev).add(userId))
      const response = await axiosInstance.post(
        API_ENDPOINTS.usersSendBatchEmails,
        { userIds: [userId] }
      )

      if (response.data.status === "success") {
        toast.success("Credentials email sent successfully")
        // Refresh the list
        await fetchPendingUsers()
      }
    } catch (error) {
      console.error("Error sending email:", error)
      toast.error(error.response?.data?.message || "Failed to send email")
    } finally {
      setResendingUsers((prev) => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  const allSelected = users.length > 0 && selectedUsers.length === users.length
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < users.length

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Credentials Email Management
              </CardTitle>
              <CardDescription>
                View daily user creations and send credentials emails in batch
              </CardDescription>
            </div>
            <Badge variant="secondary" className="text-sm">
              {users.length} Users
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div>
              <Label htmlFor="role">Role Filter</Label>
              <select
                id="role"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="mt-1 w-full md:w-64 h-10 px-3 rounded-md border border-gray-300 bg-white"
              >
                <option value="">All Roles</option>
                <option value="student">Students</option>
                <option value="teacher">Teachers</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={fetchPendingUsers} disabled={loading} variant="outline">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Calendar className="mr-2 h-4 w-4" />
                  Refresh
                </>
              )}
            </Button>
            <Button
              onClick={handleSendEmails}
              disabled={selectedUsers.length === 0 || sending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Emails ({selectedUsers.length})
                </>
              )}
            </Button>
          </div>

          {/* Results Summary */}
          {results && (
            <Alert className={results.failed > 0 ? "border-yellow-500" : "border-green-500"}>
              <AlertDescription>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="font-semibold text-green-600">{results.sent} Sent</span>
                  </div>
                  {results.failed > 0 && (
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <span className="font-semibold text-red-600">{results.failed} Failed</span>
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Users Table */}
          {users.length === 0 ? (
            <Alert>
              <AlertDescription>
                <p>No users found.</p>
              </AlertDescription>
            </Alert>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = someSelected
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Admission/Employee Code</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Email Status</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedUsers.includes(user._id)}
                          onCheckedChange={() => handleSelectUser(user._id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {user.firstName} {user.middleName || ""} {user.lastName || ""}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell className="font-mono text-sm">{user.username}</TableCell>
                      <TableCell>
                        <Badge variant={user.role === "student" ? "default" : "secondary"}>
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.admissionNumber || user.employeeCode || "N/A"}
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>
                        {user.credentialsEmailSent ? (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Done
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-orange-600 border-orange-600">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendEmail(user._id)}
                          disabled={resendingUsers.has(user._id)}
                          className="h-8"
                        >
                          {resendingUsers.has(user._id) ? (
                            <>
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              Resending...
                            </>
                          ) : (
                            <>
                              <RotateCcw className="mr-1 h-3 w-3" />
                              Resend
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

